import React, { useState } from "react";
import { Modal, ModalBody, ModalFooter, ModalHeader, Button } from "reactstrap";
import { useCustomContext } from "../../../_helper/customizer";
import { Stack } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { Upload, message } from "antd";
import { HugeiconsIcon } from "@hugeicons/react";
import { FileExcelIcon } from "@/assets/icons";
import { useImportMakkahDepartureGroupMutation } from "@/services/makkahGroups";
import { Delete02Icon } from "@hugeicons/core-free-icons";

export const ImportDepartureGroupModal = ({ isOpen, toggle }) => {
  const { layout } = useCustomContext();
  const { t } = useTranslation();
  const [selectedFile, setSelectedFile] = useState(null);
  const importMutation = useImportMakkahDepartureGroupMutation();

  const title = ({ children }) => (
    <h5 className="w-100 d-flex align-items-center justify-content-center gap-2">
      {children}
    </h5>
  );

  const dir = layout === "rtl" ? "ltr" : "rtl";

  const handleCancel = () => {
    setSelectedFile(null);
    toggle();
  };

  const beforeUpload = (file) => {
    const isExcel =
      file.type === "application/vnd.ms-excel" ||
      file.type ===
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
      file.name.endsWith(".xls") ||
      file.name.endsWith(".xlsx");

    if (!isExcel) {
      message.error(t("ONLY_EXCEL_ALLOWED"));
      return Upload.LIST_IGNORE;
    }

    const isLt10M = file.size / 1024 / 1024 < 10;
    if (!isLt10M) {
      message.error(t("MAX_FILE_10MB"));
      return Upload.LIST_IGNORE;
    }

    return false;
  };

  const handleChange = ({ fileList: newFileList }) => {
    const latestFile = newFileList.slice(-1)[0];
    setSelectedFile(latestFile || null);
  };

  const handleImport = async () => {
    if (!selectedFile || !selectedFile.originFileObj) {
      message.error(t("SELECT_EXCEL_FIRST"));
      return;
    }

    try {
      await importMutation.mutateAsync(selectedFile.originFileObj);
      setSelectedFile(null);
      toggle();
    } catch (error) {
      console.error("Import error:", error);
    }
  };

  const fileList = selectedFile ? [selectedFile] : [];

  return (
    <Modal
      size="md"
      isOpen={isOpen}
      toggle={handleCancel}
      centered
      backdrop="static"
      keyboard={false}
    >
      <ModalHeader dir={dir} toggle={handleCancel} tag={title}>
        <span className="fw-bold H5">{t("IMPORT_DEPARTURES_EXCEL")}</span>
      </ModalHeader>
      <ModalBody>
        <div className="text-center">
          {!selectedFile && (
            <Upload
              beforeUpload={beforeUpload}
              onChange={handleChange}
              fileList={fileList}
              maxCount={1}
              accept=".xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            >
              <div
                className="dropzone-container"
                style={{
                  padding: "40px 20px",
                  border: "2px dashed #7a4819",
                  borderRadius: "8px",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                }}
              >
                <div className="mb-3">
                  <FileExcelIcon style={{ width: "64px", height: "64px" }} />
                </div>
                <h5 className="mb-2">{t("CLICK_TO_SELECT_EXCEL")}</h5>
                <p className="text-muted mb-0" style={{ fontSize: "14px" }}>
                  {t("OR_DRAG_DROP")}
                </p>
                <p className="text-muted mt-2" style={{ fontSize: "12px" }}>
                  {t("SUPPORTED_FILES_EXCEL_10MB")}
                </p>
              </div>
            </Upload>
          )}

          {selectedFile && (
            <div
              className="mt-3 p-3 rounded position-relative"
              style={{ backgroundColor: "#faf8f6", color: "#6b6a61" }}
            >
              <Stack
                direction="horizontal"
                gap={2}
                className="align-items-center"
              >
                <FileExcelIcon style={{ width: "24px", height: "24px" }} />
                <div className="flex-grow-1 text-start">
                  <div className="fw-bold">{selectedFile.name}</div>
                  <div className="text-muted" style={{ fontSize: "12px" }}>
                    {(selectedFile.size / 1024).toFixed(2)} KB
                  </div>
                </div>
              </Stack>
              <Button
                className="btn btn-text btn-icon btn-danger position-absolute top-0 start-0"
                onClick={() => setSelectedFile(null)}
              >
                <HugeiconsIcon
                  className="text-danger"
                  size={16}
                  icon={Delete02Icon}
                />
              </Button>
            </div>
          )}
        </div>
      </ModalBody>
      <ModalFooter dir={dir}>
        <Stack direction="horizontal" gap={2}>
          <Button
            color="danger"
            size="xs"
            onClick={handleCancel}
            disabled={importMutation?.isPending}
          >
            {t("CANCEL")}
          </Button>
          <Button
            color="primary"
            size="xs"
            onClick={handleImport}
            disabled={!selectedFile || importMutation?.isPending}
          >
            {importMutation?.isPending ? t("IMPORTING") : t("IMPORT")}
          </Button>
        </Stack>
      </ModalFooter>
    </Modal>
  );
};
